<script setup lang="ts">
  import { watch } from "vue";
  import { useForm } from "vee-validate";
  import { toTypedSchema } from "@vee-validate/zod";
  import { z } from "zod";
  import { Input } from "@/components/ui/input";
  import { Textarea } from "@/components/ui/textarea";
  import { FormDialog } from "@/components/episto-ui/form-dialog";
  import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
  import { useScriptingGuidelinesStore } from "@stores/scriptingGuidelines";
  import { useToastsStore } from "@stores/toastsStore";
  import type { ScriptingGuideline } from "@/types";

  const props = defineProps<{ guideline: ScriptingGuideline | null; accountId: number | null }>();
  const open = defineModel<boolean>("open", { default: false });

  const store = useScriptingGuidelinesStore();
  const toast = useToastsStore();

  const { handleSubmit, meta, resetForm } = useForm({
    validationSchema: toTypedSchema(
      z.object({
        title: z.string().min(1, "Title cannot be blank"),
        body: z.string().min(1, "Rule cannot be blank"),
      }),
    ),
  });

  watch(open, (opened) => {
    if (opened) resetForm({ values: { title: props.guideline?.title ?? "", body: props.guideline?.body ?? "" } });
  });

  const save = handleSubmit(async (values) => {
    try {
      if (props.guideline) await store.update(props.guideline, values);
      else await store.create({ ...values, account_id: props.accountId });
      open.value = false;
    } catch {
      toast.error("The rule could not be saved");
    }
  });
</script>

<template>
  <FormDialog
    v-model:open="open"
    :title="guideline ? 'Edit the rule' : 'New rule'"
    :is-editing="!!guideline"
    entity-name="rule"
    :can-save="meta.valid"
    @save="save">
    <FormField
      v-slot="{ componentField }"
      name="title">
      <FormItem>
        <FormLabel>Title</FormLabel>
        <FormControl>
          <Input
            v-bind="componentField"
            placeholder="One idea per question"
            class="text-sm" />
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>
    <FormField
      v-slot="{ componentField }"
      name="body">
      <FormItem>
        <FormLabel>Rule</FormLabel>
        <FormControl>
          <Textarea
            v-bind="componentField"
            rows="3"
            class="max-h-[50vh]"
            placeholder="One to three sentences the AI can apply." />
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>
  </FormDialog>
</template>
